import { addDays, formatISO } from 'date-fns';
import { getTimezoneOffset, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import upperFirst from 'lodash.upperfirst';
import { MouseEvent } from 'react';
// types
import { RawDraftContentState, genKey } from 'draft-js';
import { IActivityFeedProfile, IMention } from 'redux-store/api/activity-feed';
import {
  HashtagMentionTypeEnum,
  ICreateActivityFeedHashtag,
  IGetActivityFeedHashtag,
  IHashtagMention,
} from 'redux-store/api/activity-feed/hashtags.types';
import { IProjectProfiles } from 'redux-store/api/projects';
import { IPhase, IPhaseItem, PhaseStatusesEnum } from 'redux-store/types';
import { hashtagColorsMap } from './constants';
import { mapHashtag, mapHashtagFirebase, mapHashtagMentionPayload } from './mappers';
import { calculateBusinessDays, postDateHelper } from './postDateHelper';

dayjs.extend(utc);
dayjs.extend(timezone);

export const getCurrentDate = (date) => {
  if (!date) {
    return null;
  }

  return dayjs.tz(date, 'UTC').format('MMM DD, YYYY');
};

export const weekDayNumbers: number[] = [1, 2, 3, 4, 5, 6, 0];

export const calculatePhaseItemProgress = (item: IPhaseItem | IPhase, workingDays?: number[]) => {
  const workDays = calculateBusinessDays(item?.startDate, item?.endDate, workingDays);

  const pastDays = item?.startDate
    ? calculateBusinessDays(item?.startDate, postDateHelper(), workingDays) - 1
    : workDays;

  const overdue = item?.startDate
    ? calculateBusinessDays(item?.endDate, postDateHelper(), workingDays) - 1
    : workDays;

  if (item?.status === PhaseStatusesEnum.COMPLETED) {
    return 100;
  } else if (overdue > 0) {
    return 101;
  } else if (pastDays <= 0 || workDays <= 0) {
    return 0;
  } else {
    return Math.round((pastDays / workDays) * 100);
  }
};

export const getInitials = (text: string) =>
  text
    .split(' ')
    .slice(0, 2)
    .map((i) => i.charAt(0).toUpperCase())
    .join('');

export const makeStringUpperFirst = (text: string) => {
  let formattedText = text.toLowerCase();

  const firstLevelCheck = formattedText.split(' ');

  for (let i = 0; i < firstLevelCheck.length; i++) {
    firstLevelCheck[i] = firstLevelCheck[i][0].toUpperCase() + firstLevelCheck[i].substring(1);
  }

  formattedText = firstLevelCheck.join(' ');

  const secondLevelCheck = formattedText.split('-');

  for (let i = 0; i < secondLevelCheck.length; i++) {
    secondLevelCheck[i] = secondLevelCheck[i][0].toUpperCase() + secondLevelCheck[i].substring(1);
  }

  formattedText = secondLevelCheck.join('-');

  const thirdLevelCheck = formattedText.split('/');

  for (let i = 0; i < thirdLevelCheck.length; i++) {
    thirdLevelCheck[i] = thirdLevelCheck[i][0].toUpperCase() + thirdLevelCheck[i].substring(1);
  }

  formattedText = thirdLevelCheck.join('/');

  const fourthLevelCheck = formattedText.split('(');

  for (let i = 0; i < fourthLevelCheck.length; i++) {
    fourthLevelCheck[i] = fourthLevelCheck[i][0].toUpperCase() + fourthLevelCheck[i].substring(1);
  }

  formattedText = fourthLevelCheck.join('(');

  return formattedText;
};

export const transformZonedTimeToUtc = (date: Date | string, tz?: string) =>
  zonedTimeToUtc(
    formatISO(new Date(date)),
    tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone
  ).toISOString();

export const transformUtcToZonedTime = (date: string, tz?: string) =>
  utcToZonedTime(date, tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone);

export const getImageBySize = (
  url: string | object,
  w: number,
  h: number,
  fitIn = true,
  temp = false
) => {
  if (typeof url === 'string' && url && url.trim() !== '') {
    try {
      // Check if it's a storage path (doesn't start with http/https) - return it directly
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        // This is a storage path, not a URL - return as-is for the frontend to convert to signed URL
        return url;
      }

      // Check if it's an emulator URL - return it directly
      if (
        url.includes('localhost:9199') ||
        url.includes(`${process.env.REACT_APP_LOCAL_IP || '127.0.0.1'}:9199`)
      ) {
        return url;
      }

      // Check if it's a Firestore Storage URL - return it directly
      if (url.includes('firebasestorage.app')) {
        return url;
      }

      const urlPathnameSplitted = new URL(url).pathname.split('/');
      const sliceIndex = urlPathnameSplitted.findIndex((str) =>
        str.includes('builderpad-backend-infra')
      );

      // If 'builderpad-backend-infra' is not found, return the original URL
      if (sliceIndex === -1) {
        return url;
      }

      const lastPart = urlPathnameSplitted.slice(sliceIndex + 1).join('/');

      return `${process.env.REACT_APP_CLOUD_FRONT}${fitIn ? '/fit-in' : ''}/${Math.ceil(
        w
      )}x${Math.ceil(h)}/${lastPart}`;
    } catch (error) {
      console.warn('Failed to process URL in getImageBySize:', url, error);
      // Return the original URL if processing fails
      return url;
    }
  }

  // Return empty string for invalid/empty URLs
  return '';
};

export const addDay = (date: string, days: number) => {
  return addDays(new Date(date), days);
};

export const weekDays = [0, 1, 2, 3, 4, 5, 6];

export const toggleClassByTagName = (tagName: string, className: string) => {
  const element = document.getElementsByTagName(tagName)[0];
  if (element) {
    if (element.classList.contains(className)) element.classList.remove(className);
    else element.classList.add(className);
  }
};

export const getTimeSpend = (date: string) => {
  return date
    .split(' ')
    .map((item, index) => {
      if (index === 1) {
        return item === 'months' ? 'mo' : item[0];
      }
      return item;
    })
    .join('')
    .concat(' ago');
};

export const convertBytes = (bytes: number, decimals = 1) => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)).toString(), 10);
  if (i === 0) return `${bytes} ${sizes[i]}`;
  return `${(bytes / 1024 ** i).toFixed(decimals)} ${sizes[i]}`;
};

export const convertHtml = (htmlString: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML =
    htmlString && !htmlString.startsWith('<p>') ? `<p>${htmlString}</p>` : htmlString;

  return Array.from(tempDiv.children ?? []).map((item) => item.textContent);
};

type EntityItem =
  | (IMention & { entityType: 'mention' })
  | (IGetActivityFeedHashtag & { entityType: 'hashtag' });

export const createContent = (
  content: string,
  mentions: IMention[],
  hashtags: IGetActivityFeedHashtag[]
) => {
  return convertHtml(content)
    .map((item, index) => {
      const allEntities: EntityItem[] = [
        ...(mentions
          ?.filter(({ block }) => block === index)
          ?.map((m) => ({ ...m, entityType: 'mention' as const })) ?? []),
        ...(hashtags
          ?.filter(({ block }) => block === index)
          ?.map((h) => ({ ...h, entityType: 'hashtag' as const })) ?? []),
      ];

      if (!allEntities.length) {
        return `<p>${item}</p>`;
      }
      const itemsArr = item.split('');
      let indexChange = 0;

      allEntities.sort((a, b) => a.offset - b.offset);

      allEntities.forEach((entity) => {
        const { offset, length, entityType } = entity;
        const newOffset = offset - indexChange;
        let text = itemsArr.slice(newOffset, newOffset + length).join('');

        if (entityType === 'mention') {
          const value = `<span style="color: #4D6EF5;">${text}</span>`;
          itemsArr.splice(newOffset, length, value);
          indexChange += length - 1;
        }

        if (entityType === 'hashtag') {
          text = text.replace(/^#/, '');

          const map = hashtagColorsMap[entity.type];
          const { color, background, iconUrl } = map;
          const hashtag = mapHashtag(entity);
          const { type } = hashtag;

          const id =
            type === HashtagMentionTypeEnum.selectionOption ? hashtag.selectionItemId : hashtag.id;
          const mimeType =
            type === HashtagMentionTypeEnum.fileCabinetItem
              ? `data-mimeType=${hashtag.mimeType}`
              : '';

          const value = `
            <span
              data-hashtag-id="${id}"
              data-hashtag-type="${type}"
              ${mimeType}
              data-mention-type="hashtag"
              style="
                cursor: pointer;
                display: inline-flex;
                align-items: center;
                gap: 4px;
                background: ${background};
                padding: 0 4px;
                border-radius: 8px;
                vertical-align: middle;
              "
            >
                ${iconUrl}
              <span
                style="
                  display: inline;
                  vertical-align: middle;
                  font-family: Inter, sans-serif;
                  font-weight: 400;
                  font-size: 14px;
                  line-height: 146%;
                  color: ${color};
                "
              >
                ${text}
              </span>
            </span>
          `;

          itemsArr.splice(newOffset, length, value);
          indexChange += length - 1;
        }
      });

      return `<p>${itemsArr.join('')}</p>`;
    })
    .join('');
};

export const createDraftContent = ({
  profiles,
  blocksContent,
  mentions,
  hashtags,
}: {
  profiles: IProjectProfiles[];
  blocksContent: string[];
  mentions: IMention[];
  hashtags?: IGetActivityFeedHashtag[];
}) => {
  const allEntities: EntityItem[] = [
    ...(hashtags || []).map((h) => ({ ...h, entityType: 'hashtag' as const })),
    ...mentions.map((m) => ({ ...m, entityType: 'mention' as const })),
  ];

  const entityMap = Object.fromEntries(
    allEntities.map((entity, idx) => {
      const key = idx.toString();

      if (entity.entityType === 'hashtag') {
        return [
          key,
          {
            type: '#mention',
            mutability: 'IMMUTABLE',
            data: {
              mention: mapHashtag(entity),
            },
          },
        ];
      }

      // type === 'mention'
      const user = profiles.find((p) => p.id === entity.profileId);
      if (!user) return [key, {}];

      const { avatar, email, id, firstName, lastName } = user;
      return [
        key,
        {
          type: 'mention',
          mutability: 'IMMUTABLE',
          data: {
            mention: {
              id,
              avatar,
              email,
              firstName,
              lastName,
              name: `${firstName} ${lastName}`,
            },
          },
        },
      ];
    })
  ) as RawDraftContentState['entityMap'];

  const blocks = blocksContent.map((text, blockIndex) => {
    const entityRanges = allEntities
      .map((e, idx) => ({ e, idx }))
      .filter(({ e }) => e.block === blockIndex)
      .map(({ e, idx }) => ({
        offset: e.offset,
        length: e.length,
        key: idx,
      }));

    return {
      key: genKey(),
      text,
      type: 'unstyled',
      data: {},
      depth: 0,
      inlineStyleRanges: [],
      entityRanges,
    };
  });

  return { blocks, entityMap };
};

export const createMentionsArray = (contentState: RawDraftContentState) =>
  contentState.blocks
    ?.map(({ entityRanges }, block) =>
      entityRanges?.length
        ? entityRanges.map(({ offset, length, key }) => {
            const profileId = contentState.entityMap[key.toString()].data?.mention?.id;

            if (
              typeof profileId !== 'number' ||
              contentState.entityMap[key.toString()].type === '#mention'
            ) {
              return;
            }

            return {
              offset,
              length,
              key,
              profileId,
              block,
            };
          })
        : []
    )
    .flat()
    .filter((item) => item)
    .map((mention, idx) => ({
      ...mention,
      key: idx,
    }));

export const getHashtagsArray = (contentState: RawDraftContentState): IGetActivityFeedHashtag[] =>
  contentState.blocks
    .map(({ entityRanges }, block) => {
      return entityRanges.map((entityRange) => {
        const key = entityRange.key;
        if (contentState.entityMap[key.toString()].type === 'mention') {
          return;
        }

        if (!contentState.entityMap[key.toString()].data?.mention) return;

        const entityMention: IHashtagMention = contentState.entityMap[key.toString()].data?.mention;

        return mapHashtagFirebase({ entityMention, entityRange, block });
      });
    })
    .flat()
    .filter((item) => item)
    .map((mention, idx) => ({
      ...mention,
      key: idx,
    }));

export const createHashtagsArray = (
  contentState: RawDraftContentState
): ICreateActivityFeedHashtag[] =>
  contentState.blocks
    .map(({ entityRanges }, block) =>
      entityRanges.map((entityRange) => {
        const key = entityRange.key;
        if (contentState.entityMap[key.toString()].type === 'mention') {
          return;
        }

        const entityMention: IHashtagMention = contentState.entityMap[key.toString()].data?.mention;

        return mapHashtagMentionPayload({ entityMention, entityRange, block });
      })
    )
    .flat()
    .filter((item) => item)
    .map((mention, idx) => ({
      ...mention,
      key: idx,
    }));

export const createMentionToContentHelper = (
  profile: IActivityFeedProfile
): { textContent: string; mentionEntity: IMention } => {
  const textContent = `@${profile.firstName} ${profile.lastName} `;
  const mentionEntity = {
    key: 0,
    block: 0,
    offset: 0,
    length: textContent.length - 1,
    profileId: profile.id,
  };
  return { textContent, mentionEntity };
};

export const isContentEmpty = (content: string) => content.replace(/\s/g, '') === '<p></p>';

export const convertHour = (convertTo: 'utc' | 'locale', hour: number, tz: string | number) => {
  const offset =
    typeof tz === 'string'
      ? getTimezoneOffset(tz, new Date()) / (60 * 60 * 1000)
      : Math.round(tz / 60);

  const convertedHour = convertTo === 'locale' ? hour + offset : hour - offset;

  return convertedHour >= 24
    ? convertedHour - 24
    : convertedHour < 0
      ? convertedHour + 24
      : convertedHour;
};

export const getUpperFirstWithoutUnderline = (str: string) =>
  upperFirst(str.split('_').join(' ').toLowerCase());

export const stopPropagation = (e: MouseEvent<HTMLElement>) => {
  e.stopPropagation();
};

export const callHandlerWithoutPropagation =
  <T extends any[] = any[], R = any>(cb: (...args: T) => R, args?: T) =>
  (event: MouseEvent<HTMLElement>) => {
    stopPropagation(event);

    cb(...(args ?? ([] as T)));
  };

export const convertCamelCaseToTitleCase = (input: string) =>
  input.replace(/([A-Z])/g, ' $1').replace(/^./, function (str) {
    return str.toUpperCase();
  });

export const handleCheckProjectEndDate = (phases: IPhase[]) => {
  if (phases?.length === 0) {
    return null;
  }

  let maxEndDateObject = phases?.[0];

  for (let i = 1; i < phases?.length; i++) {
    const currentObject = phases?.[i];
    if (currentObject?.endDate > maxEndDateObject.endDate) {
      maxEndDateObject = currentObject;
    }
  }

  return maxEndDateObject?.endDate;
};

export const getCompanyId = (): number | null => {
  const companyId = localStorage.getItem('company-id');
  return companyId ? Number(companyId) : null;
};

export const getCapitalizedString = (str: string) => upperFirst(str.toLowerCase());
